// Simple-mode filters (§11.19): 33 built-in looks in 5 groups. Each filter is a list of changes on top of
// the photo's current settings ("add" moves a value, "set" goes to a value), so cleaners stay intact and
// the Strength slider (0–100%) scales every change. Starting values; the owner tunes them on real photos.
// Rule (tested): food filters never move the green or yellow hue by more than 5.
// The Mega Food Pack (foodPack.ts, 40 filters) and Food Pack 2 (foodPack2.ts, 32 filters) join the food group
// in the same 4 sections; the 10 food filters below are its "Originals" tab.
import { monotoneCubic } from '../color/curve';
import type { Pt, Settings } from '../model/types';
import { FOOD_PACK, type FoodSection } from './foodPack';
import { FOOD_PACK_2 } from './foodPack2';

export type FilterGroup = 'food' | 'everyday' | 'mood' | 'people' | 'bw';
type Op = [path: string, op: 'add' | 'set', value: number];
export type SimpleFilter = {
  id: string;
  group: FilterGroup;
  name: string;
  nameEs: string;
  ops: Op[];
  /** Master tone curve the filter moves toward (e.g. a matte lift), blended by strength. */
  curve?: Pt[];
  /** Food pack section; absent = the food group's "Originals". */
  section?: FoodSection;
};

const RANGES: [RegExp, number, number][] = [
  [/^light\.exposure$/, -5, 5],
  [/Hue$/, 0, 360],
  [/^effects\.(grain|splitTone)\.\w*(amount|Sat|size)$/, 0, 100],
  [/^cleanWhites\.amount$/, 0, 100],
  [/^detail\.sharpAmount$/, 0, 150],
  [/^detail\.(nrLuma|nrColor|sharpMasking)$/, 0, 100],
  [/^effects\.vignette\.(midpoint|feather)$/, 0, 100],
  [/.*/, -100, 100],
];
export const clampFor = (path: string, v: number) => {
  const r = RANGES.find(([re]) => re.test(path))!;
  return Math.min(r[2], Math.max(r[1], v));
};
export const getPath = (o: unknown, path: string): number =>
  path.split('.').reduce<unknown>((a, k) => (a as Record<string, unknown>)[k], o) as number;
function setPath(o: unknown, path: string, v: number) {
  const keys = path.split('.');
  const last = keys.pop()!;
  const parent = keys.reduce<unknown>((a, k) => (a as Record<string, unknown>)[k], o) as Record<
    string,
    number
  >;
  parent[last] = v;
}

const f = (
  id: string,
  group: FilterGroup,
  name: string,
  nameEs: string,
  ops: Op[],
  curve?: Pt[],
): SimpleFilter => ({ id, group, name, nameEs, ops, ...(curve ? { curve } : {}) });
const lift = (black: number, white = 1): Pt[] => [
  { x: 0, y: black },
  { x: 1, y: white },
];

const BASE_FILTERS: SimpleFilter[] = [
  // Food (§11.18 looks + 6 more): the "Originals" tab of the food group
  f('fresh', 'food', 'Fresh', 'Fresco', [
    ['light.exposure', 'add', 0.15],
    ['light.contrast', 'add', 10],
    ['light.highlights', 'add', -20],
    ['light.shadows', 'add', 15],
    ['color.vibrance', 'add', 20],
    ['wb.temp', 'add', -3],
    ['hsl.green.s', 'add', 10],
    ['hsl.green.l', 'add', 5],
    ['hsl.yellow.s', 'add', 5],
    ['detail.sharpAmount', 'add', 40],
  ]),
  f('warm-plate', 'food', 'Warm Plate', 'Plato cálido', [
    ['wb.temp', 'add', 12],
    ['wb.tint', 'add', 3],
    ['light.contrast', 'add', 8],
    ['light.shadows', 'add', 10],
    ['color.vibrance', 'add', 15],
    ['hsl.orange.s', 'add', 8],
    ['hsl.blue.s', 'add', -15],
  ]),
  f('bright-menu', 'food', 'Bright Menu', 'Menú brillante', [
    ['light.exposure', 'add', 0.35],
    ['light.highlights', 'add', -30],
    ['light.shadows', 'add', 25],
    ['light.whites', 'add', 15],
    ['light.blacks', 'add', 5],
    ['color.vibrance', 'add', 25],
    ['cleanWhites.amount', 'add', 30],
  ]),
  f('dark-moody', 'food', 'Dark Moody', 'Oscuro', [
    ['light.exposure', 'add', -0.3],
    ['light.contrast', 'add', 20],
    ['light.highlights', 'add', -40],
    ['light.shadows', 'add', -10],
    ['light.blacks', 'add', -15],
    ['color.vibrance', 'add', 10],
    ['color.saturation', 'add', -5],
    ['wb.temp', 'add', 5],
  ]),
  f('golden-hour', 'food', 'Golden Hour', 'Hora dorada', [
    ['wb.temp', 'add', 15],
    ['wb.tint', 'add', 3],
    ['light.highlights', 'add', -25],
    ['light.shadows', 'add', 10],
    ['color.vibrance', 'add', 10],
    ['hsl.orange.s', 'add', 8],
    ['effects.splitTone.highlightHue', 'set', 50],
    ['effects.splitTone.highlightSat', 'add', 15],
  ]),
  f('crisp-salad', 'food', 'Crisp Salad', 'Ensalada fresca', [
    ['wb.temp', 'add', -6],
    ['light.contrast', 'add', 10],
    ['color.vibrance', 'add', 15],
    ['hsl.green.s', 'add', 15],
    ['hsl.green.l', 'add', 5],
    ['cleanWhites.amount', 'add', 30],
    ['detail.sharpAmount', 'add', 30],
  ]),
  f(
    'coffee-house',
    'food',
    'Coffee House',
    'Cafetería',
    [
      ['wb.temp', 'add', 10],
      ['color.saturation', 'add', -15],
      ['light.contrast', 'add', 8],
      ['hsl.orange.l', 'add', -5],
    ],
    lift(0.05),
  ),
  f(
    'dessert-pop',
    'food',
    'Dessert Pop',
    'Postre pop',
    [
      ['light.exposure', 'add', 0.3],
      ['light.shadows', 'add', 20],
      ['color.vibrance', 'add', 20],
      ['hsl.magenta.s', 'add', 15],
      ['hsl.red.s', 'add', 5],
      ['hsl.magenta.l', 'add', 5],
    ],
    lift(0.04),
  ),
  f('street-food', 'food', 'Street Food', 'Comida callejera', [
    ['light.contrast', 'add', 20],
    ['color.vibrance', 'add', 25],
    ['wb.temp', 'add', 8],
    ['detail.sharpAmount', 'add', 30],
  ]),
  f('seafood-fresh', 'food', 'Seafood Fresh', 'Mariscos frescos', [
    ['wb.temp', 'add', -10],
    ['hsl.aqua.s', 'add', 12],
    ['hsl.blue.s', 'add', 10],
    ['cleanWhites.amount', 'add', 40],
    ['color.vibrance', 'add', 10],
  ]),
  // Everyday
  f('natural', 'everyday', 'Natural', 'Natural', [
    ['color.vibrance', 'add', 8],
    ['light.contrast', 'add', 5],
  ]),
  f('vivid', 'everyday', 'Vivid', 'Vívido', [
    ['color.vibrance', 'add', 35],
    ['color.saturation', 'add', 10],
    ['light.contrast', 'add', 12],
  ]),
  f('soft', 'everyday', 'Soft', 'Suave', [
    ['light.contrast', 'add', -15],
    ['light.highlights', 'add', -15],
    ['light.shadows', 'add', 15],
  ]),
  f('warm', 'everyday', 'Warm', 'Cálido', [['wb.temp', 'add', 15]]),
  f('cool', 'everyday', 'Cool', 'Frío', [['wb.temp', 'add', -15]]),
  f('clear-day', 'everyday', 'Clear Day', 'Día despejado', [
    ['light.exposure', 'add', 0.1],
    ['light.contrast', 'add', 10],
    ['light.highlights', 'add', -20],
    ['light.shadows', 'add', 10],
    ['wb.temp', 'add', -4],
    ['color.vibrance', 'add', 15],
    ['hsl.blue.s', 'add', 10],
  ]),
  f('bright-airy', 'everyday', 'Bright & Airy', 'Luminoso', [
    ['light.exposure', 'add', 0.4],
    ['light.contrast', 'add', -10],
    ['light.shadows', 'add', 25],
    ['light.highlights', 'add', -15],
    ['color.saturation', 'add', -5],
    ['wb.temp', 'add', -3],
  ]),
  f('rich', 'everyday', 'Rich', 'Intenso', [
    ['light.contrast', 'add', 18],
    ['light.blacks', 'add', -10],
    ['color.vibrance', 'add', 20],
    ['color.saturation', 'add', 5],
  ]),
  // Mood
  f(
    'film',
    'mood',
    'Film',
    'Película',
    [
      ['light.contrast', 'add', -8],
      ['color.saturation', 'add', -10],
      ['effects.grain.amount', 'add', 25],
      ['effects.splitTone.shadowHue', 'set', 200],
      ['effects.splitTone.shadowSat', 'add', 10],
      ['effects.splitTone.highlightHue', 'set', 50],
      ['effects.splitTone.highlightSat', 'add', 10],
    ],
    lift(0.05, 0.96),
  ),
  f(
    'faded',
    'mood',
    'Faded',
    'Desvanecido',
    [
      ['light.contrast', 'add', -20],
      ['color.saturation', 'add', -20],
    ],
    lift(0.1, 0.95),
  ),
  f(
    'vintage',
    'mood',
    'Vintage',
    'Antiguo',
    [
      ['wb.temp', 'add', 12],
      ['color.saturation', 'add', -25],
      ['effects.vignette.amount', 'add', -30],
      ['effects.grain.amount', 'add', 30],
      ['effects.splitTone.highlightHue', 'set', 45],
      ['effects.splitTone.highlightSat', 'add', 20],
    ],
    lift(0.08),
  ),
  f('matte', 'mood', 'Matte', 'Mate', [['light.contrast', 'add', -5]], lift(0.08)),
  f('cinematic', 'mood', 'Cinematic', 'Cine', [
    ['light.contrast', 'add', 10],
    ['color.vibrance', 'add', 5],
    ['effects.splitTone.shadowHue', 'set', 195],
    ['effects.splitTone.shadowSat', 'add', 30],
    ['effects.splitTone.highlightHue', 'set', 40],
    ['effects.splitTone.highlightSat', 'add', 25],
  ]),
  f('sunset', 'mood', 'Sunset', 'Atardecer', [
    ['wb.temp', 'add', 25],
    ['wb.tint', 'add', 8],
    ['color.vibrance', 'add', 15],
    ['hsl.orange.s', 'add', 15],
    ['hsl.red.s', 'add', 10],
    ['effects.vignette.amount', 'add', -15],
  ]),
  f('night-glow', 'mood', 'Night Glow', 'Brillo nocturno', [
    ['light.exposure', 'add', 0.2],
    ['light.shadows', 'add', 30],
    ['light.blacks', 'add', -10],
    ['wb.temp', 'add', -10],
    ['effects.splitTone.shadowHue', 'set', 260],
    ['effects.splitTone.shadowSat', 'add', 25],
    ['effects.splitTone.highlightHue', 'set', 40],
    ['effects.splitTone.highlightSat', 'add', 15],
    ['effects.vignette.amount', 'add', -25],
  ]),
  // People (skin hues are protected by vibrance; these avoid big orange/red saturation moves)
  f('soft-portrait', 'people', 'Soft Portrait', 'Retrato suave', [
    ['light.contrast', 'add', -10],
    ['light.highlights', 'add', -10],
    ['light.shadows', 'add', 10],
    ['wb.temp', 'add', 5],
    ['hsl.orange.s', 'add', -5],
  ]),
  f(
    'glow',
    'people',
    'Glow',
    'Resplandor',
    [
      ['light.shadows', 'add', 25],
      ['light.highlights', 'add', -15],
      ['light.exposure', 'add', 0.15],
      ['light.contrast', 'add', -5],
      ['light.whites', 'add', 10],
    ],
    lift(0.03),
  ),
  f('fresh-face', 'people', 'Fresh Face', 'Rostro fresco', [
    ['light.exposure', 'add', 0.15],
    ['light.contrast', 'add', 6],
    ['color.vibrance', 'add', 10],
    ['hsl.orange.l', 'add', 6],
  ]),
  // Black & white (saturation −100; tones come from split toning, which runs after Color)
  f('bw-classic', 'bw', 'Classic', 'Clásico', [
    ['color.saturation', 'add', -100],
    ['light.contrast', 'add', 10],
  ]),
  f('bw-high-contrast', 'bw', 'High Contrast', 'Alto contraste', [
    ['color.saturation', 'add', -100],
    ['light.contrast', 'add', 35],
    ['light.blacks', 'add', -15],
    ['light.whites', 'add', 10],
  ]),
  f('bw-soft', 'bw', 'Soft', 'Suave', [
    ['color.saturation', 'add', -100],
    ['light.contrast', 'add', -15],
    ['light.shadows', 'add', 15],
  ]),
  f('bw-sepia', 'bw', 'Sepia', 'Sepia', [
    ['color.saturation', 'add', -100],
    ['effects.splitTone.shadowHue', 'set', 40],
    ['effects.splitTone.shadowSat', 'add', 35],
    ['effects.splitTone.highlightHue', 'set', 50],
    ['effects.splitTone.highlightSat', 'add', 30],
  ]),
  f('bw-selenium', 'bw', 'Selenium', 'Selenio', [
    ['color.saturation', 'add', -100],
    ['light.contrast', 'add', 8],
    ['effects.splitTone.shadowHue', 'set', 260],
    ['effects.splitTone.shadowSat', 'add', 20],
    ['effects.splitTone.highlightHue', 'set', 220],
    ['effects.splitTone.highlightSat', 'add', 10],
  ]),
];

export const SIMPLE_FILTERS: SimpleFilter[] = [...BASE_FILTERS, ...FOOD_PACK, ...FOOD_PACK_2];

export const FILTER_GROUPS: FilterGroup[] = ['food', 'everyday', 'mood', 'people', 'bw'];

/** Tabs inside the food group: the original 10, then the four food pack sections (Mega Food Pack, then Food Pack 2). */
export type FoodTab = 'originals' | FoodSection;
export const FOOD_TABS: FoodTab[] = ['originals', 'fix', 'dish', 'style', 'menu'];
export const foodTab = (f: SimpleFilter): FoodTab => f.section ?? 'originals';

/** `base` with filter `f` applied at strength k (0..1). Pure. */
export function applyFilter(base: Settings, fl: SimpleFilter, k: number): Settings {
  const out = structuredClone(base);
  for (const [path, op, v] of fl.ops) {
    const b = getPath(base, path);
    setPath(out, path, clampFor(path, op === 'add' ? b + k * v : b + k * (v - b)));
  }
  if (fl.curve) {
    const baseCurve = monotoneCubic(base.curve.master);
    out.curve.master = fl.curve.map((p) => ({
      x: p.x,
      y: Math.min(1, Math.max(0, baseCurve(p.x) + k * (p.y - p.x))),
    }));
  }
  return out;
}

/** Paths a filter touches (for "what changed" checks and Pro-mode parity tests). */
export const filterPaths = (fl: SimpleFilter) => [
  ...fl.ops.map((o) => o[0]),
  ...(fl.curve ? ['curve.master'] : []),
];
